import React, { useEffect, useState } from 'react'
import { Badge, Form, InputGroup } from 'react-bootstrap'
import styled from 'styled-components'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { DateRangePicker } from 'react-date-range'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarWeek } from '@fortawesome/pro-light-svg-icons'
import { useSearchParams } from 'react-router-dom'

import GlobalStyles from '../../../../components/component-items/styles'
import { TableCell } from '../../../../components/component-items/datatable'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import { useFetch } from '../../../../components/component-items/helpers'
import CardLoading from '../../../../components/component-items/loading-popover'
import { Pages } from '../../../../components/component-items/pager'
import { friendlyValue } from './wave-utils'

type RunHistoryBatchBreakdown = {
  label: string
  value: number
}

type RunHistoryRow = {
  id: string
  api_id?: string
  profile_id: string
  profile: string
  directive: string
  requested_at: string
  started_at: string
  requested_by: string
  duration: string
  selected: number
  reserved: number
  not_reserved: number
  batches_created: number
  batch_breakdown: RunHistoryBatchBreakdown[]
  locations_touched: number
  zones_touched: number
  pick_distance_feet: number | null
  average_pick_distance_feet: number | null
  product_variants: number
  units_reserved: number
  source_mix: string
  status: string
  statusFilter: string
  failure_reason: string
}

type RunHistoryBatch = {
  batch?: string | number
  batch_type?: string
  order_count?: number
  pick_distance_feet: number | null
}

const RunHistoryLayout = styled.div`
  position: relative;

  .filter-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: flex-end;
    margin: 0.75em 1em 0.75em;
    padding: 1rem;
    border-radius: 8px;
    background: #f8f9fa;
  }

  .filter-clear-button {
    flex: 0 0 auto;
    align-self: flex-end;
    min-width: 96px;
    height: 34px;
    padding-top: 0;
    padding-bottom: 0;
    line-height: 32px;
    margin: 0;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 150px;

    label {
      color: #6c757d;
      font-size: 0.8em;
      font-weight: 500;
    }

    .form-select,
    .form-control,
    select,
    input {
      height: 34px !important;
      font-size: 0.9rem;
    }
  }

  .date-input-group {
    position: relative;

    .form-control {
      padding-right: 2.75em;
    }
  }

  .date-range-picker {
    position: absolute;
    top: 128px;
    left: 1em;
    z-index: 50;
    overflow: scroll;
    box-shadow: 0px 0px 13px 0px rgba(82, 63, 105, 0.19);
    width: fit-content;
    border-radius: 4px;
    background: white;
  }

  .date-range-backdrop {
    position: absolute;
    inset: 0;
    z-index: 40;
    background: transparent;
  }

  .summary-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(140px, 1fr));
    gap: 1em;
    padding: 0.75em 1em 1.25em;
  }

  .summary-card {
    padding: 1em;
    text-align: left;

    p {
      margin: 0;
      color: #74788d;
      font-size: 0.85rem;
      font-weight: 600;
      text-transform: uppercase;
    }

    h4 {
      margin: 0.35em 0 0;
      color: #495057;
      font-size: 1.35rem;
    }
  }

  .history-helper {
    margin: 0.75em 1em 0;
    color: #74788d;
    font-size: 0.9rem;
  }

  .history-table-wrap {
    padding: 0 1em 1em;
  }

  .pagination-row {
    display: flex;
    justify-content: flex-end;
    padding: 0 1em 1em;
  }

  .history-table {
    border-collapse: collapse;

    thead th {
      padding: 0.75em 0.9em;
      background: #fbfcff;
      border-bottom: 1px solid #ebedf2;
      color: #74788d;
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    tbody tr:hover {
      background: #f6f9ff;
    }
  }

  .run-link-button {
    border: 0;
    background: transparent;
    color: #5d78ff;
    font-weight: 700;
    padding: 0;
    text-decoration: underline;
  }

  .run-detail-panel {
    position: relative;
    margin: 0 1em 1em;
    padding: 1rem;
    border: 1px solid #ebedf2;
    border-radius: 8px;
    background: #fbfcff;
  }

  .run-detail-header {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: flex-start;
    margin-bottom: 1rem;
  }

  .run-detail-title {
    h4 {
      margin: 0;
      color: #495057;
      font-size: 1rem;
    }

    p {
      margin: 0.25em 0 0;
      color: #74788d;
      font-size: 0.9rem;
    }
  }

  .run-detail-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(140px, 1fr));
    gap: 0.75rem;
  }

  .run-detail-card {
    border: 1px solid #ebedf2;
    border-radius: 6px;
    background: white;
    padding: 0.75rem;

    label {
      display: block;
      margin-bottom: 0.35em;
      color: #74788d;
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
  }

  .run-detail-list {
    display: flex;
    flex-direction: column;
    gap: 0.35em;
  }

  .pill-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5em;
    justify-content: center;
    margin-top: 0.35em;
  }

  .pill-list.left {
    justify-content: flex-start;
  }

  .metric-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    background: #f6f9ff;
    color: #495057;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.35em 0.75em;
    white-space: nowrap;
  }

  .metric-list {
    display: flex;
    flex-direction: column;
    gap: 0.25em;
    line-height: 1.35;
  }

  .detail-line {
    color: #495057;
    font-size: 0.9rem;

    &.muted {
      color: #74788d;
    }
  }

  .detail-group {
    margin-top: 0.35em;
    padding-top: 0.35em;
    border-top: 1px solid #ebedf2;
  }

  .cell-primary {
    font-weight: 600;
  }

  .cell-primary.strong {
    color: #495057;
    font-size: 0.95rem;
  }

  .cell-secondary {
    color: #74788d;
    font-size: 0.9rem;
  }

  .duration-running {
    color: #5d78ff;
    font-weight: 700;
  }

  @media (max-width: 900px) {
    .summary-row {
      grid-template-columns: 1fr;
    }

    .run-detail-grid {
      grid-template-columns: 1fr;
    }
  }
`

const getLocalDateValue = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const dateStringToLocalDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number)
  return year && month && day ? new Date(year, month - 1, day) : new Date()
}

const formatDateInputValue = (date: Date) => date.toLocaleDateString('en-US').replace(/\//g, '-')

const formatRunDate = (date: string) => {
  if (!date) {
    return { day: 'Not started', time: '-' }
  }
  const runDate = new Date(date)
  return {
    day: runDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: runDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  }
}

const formatElapsedDuration = (startDate: string, now: Date) => {
  const startedAt = new Date(startDate)
  if (!startDate || Number.isNaN(startedAt.getTime())) return '-'
  const elapsedSeconds = Math.max(0, Math.floor((now.getTime() - startedAt.getTime()) / 1000))
  const hours = Math.floor(elapsedSeconds / 3600)
  const minutes = Math.floor((elapsedSeconds % 3600) / 60)
  const seconds = elapsedSeconds % 60

  if (hours) return `${hours}h ${minutes}m`
  if (minutes) return `${minutes}m ${String(seconds).padStart(2, '0')}s`
  return `${seconds}s`
}

const formatPickDistance = (feet: number | null) => {
  if (feet == null) return 'NA'
  return `${feet.toLocaleString()} ft est.`
}

const formatDuration = (startedAt?: string, completedAt?: string) => {
  if (!startedAt || !completedAt) return '-'
  return formatElapsedDuration(startedAt, new Date(completedAt))
}

const titleizeStatus = (status: string) => friendlyValue(status || 'queued')

const batchLabel = (key: string) => {
  const labels: Record<string, string> = {
    prepack: 'Prepack',
    pick_sort: 'Pick & Sort',
    pick_and_sort: 'Pick & Sort',
    fastpick: 'Fastpick',
    single_item: 'Single Item',
    single_item_pick: 'Single Item',
  }
  return labels[key] || key
}

const normalizeBatchBreakdown = (value: any): RunHistoryBatchBreakdown[] => {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'object') return []
  return Object.entries(value)
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => ({ label: batchLabel(key), value: Number(count) }))
}

const normalizeSourceMix = (value: any) => {
  if (typeof value === 'string') return value
  if (!Array.isArray(value)) return ''
  return value
    .filter((source) => Number(source.quantity) > 0)
    .map((source) => `${friendlyValue(source.source)} ${source.quantity}`)
    .join(' / ')
}

const normalizeRun = (run: any): RunHistoryRow => {
  const statusFilter = String(run.statusFilter || run.status || 'queued').toLowerCase()
  const requestedAt = run.requested_at || run.created || run.started_at
  return {
    id: run.id && String(run.id).startsWith('WR-') ? run.id : `WR-${run.id}`,
    api_id: run.api_id || String(run.id || ''),
    profile_id: run.profile_id ? String(run.profile_id) : 'one-off',
    profile: run.profile || run.profile_label || 'One-off Wave Directive',
    directive: run.directive_name || 'One-off Wave Directive',
    requested_at: requestedAt,
    started_at: run.started_at || '',
    requested_by: run.requested_by_name || 'System',
    duration: run.duration || formatDuration(run.started_at, run.completed_at),
    selected: run.selected || 0,
    reserved: run.reserved || 0,
    not_reserved: run.not_reserved || 0,
    batches_created: run.batches_created || 0,
    batch_breakdown: normalizeBatchBreakdown(run.batch_breakdown),
    locations_touched: run.locations_touched || 0,
    zones_touched: run.zones_touched || 0,
    pick_distance_feet: run.pick_distance_feet ?? null,
    average_pick_distance_feet: run.average_pick_distance_feet ?? null,
    product_variants: run.product_variants || 0,
    units_reserved: run.units_reserved || 0,
    source_mix: normalizeSourceMix(run.source_mix),
    status: run.status && run.status !== statusFilter ? run.status : titleizeStatus(statusFilter),
    statusFilter,
    failure_reason: run.failure_reason || '',
  }
}

const normalizeRunResponse = (response: any): RunHistoryRow[] => {
  if (Array.isArray(response)) return response.map(normalizeRun)
  if (Array.isArray(response?.results)) return response.results.map(normalizeRun)
  return []
}

const outcomeLabel = (value: string) => {
  if (!value) return 'Unknown'
  return friendlyValue(value)
}

const summarizeOrderOutcomes = (orders: any[]) => {
  const counts: Record<string, number> = {}
  orders.forEach((order) => {
    const key = outcomeLabel(order.outcome)
    counts[key] = (counts[key] || 0) + 1
  })
  return Object.entries(counts).map(([label, value]) => ({ label, value }))
}

const summarizeRunBatches = (batches: any[]) =>
  batches.map((batch) => ({
    label: batchLabel(batch.batch_type || 'batch'),
    value: Number(batch.order_count || 0),
  }))

const statusClassName = (status: string) => {
  if (status === 'failed') return 'red'
  if (status === 'running') return 'blue'
  if (status === 'queued') return 'silver'
  return 'green'
}

export const WaveRunHistoryTable = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const today = new Date()
  const initialStartDate = searchParams.get('start')
  const initialEndDate = searchParams.get('end')
  const [profileId, setProfileId] = useState(searchParams.get('profile') || 'all')
  const [status, setStatus] = useState(searchParams.get('status') || 'all')
  const [page, setPage] = useState(Number(searchParams.get('page') || 1))
  const [selectedRun, setSelectedRun] = useState<RunHistoryRow | null>(null)
  const [showDateRange, setShowDateRange] = useState(false)
  const [dateInputValue, setDateInputValue] = useState(
    initialStartDate && initialEndDate
      ? `${formatDateInputValue(dateStringToLocalDate(initialStartDate))} - ${formatDateInputValue(
          dateStringToLocalDate(initialEndDate)
        )}`
      : 'Today'
  )
  const [now, setNow] = useState(new Date())
  const detailUrl = selectedRun?.api_id ? `/api/inventory/wave-directive/runs/${selectedRun.api_id}/` : ''
  const { response: detailResponse, loaded: detailLoaded, error: detailError }: any = useFetch(detailUrl, {})
  const [dateRange, setDateRange] = useState([
    {
      startDate: initialStartDate ? dateStringToLocalDate(initialStartDate) : today,
      endDate: initialEndDate ? dateStringToLocalDate(initialEndDate) : today,
      key: 'selection',
    },
  ])

  const selectedStartDate = dateInputValue ? getLocalDateValue(dateRange[0].startDate) : ''
  const selectedEndDate = dateInputValue ? getLocalDateValue(dateRange[0].endDate) : ''

  const runHistoryParams = new URLSearchParams()
  runHistoryParams.set('page', String(page))
  runHistoryParams.set('page_size', '25')
  if (profileId !== 'all') runHistoryParams.set('profile', profileId)
  if (status !== 'all') runHistoryParams.set('status', status)
  if (selectedStartDate) runHistoryParams.set('start', selectedStartDate)
  if (selectedEndDate) runHistoryParams.set('end', selectedEndDate)

  const { response, loaded, error }: any = useFetch(
    `/api/inventory/wave-directive/runs/?${runHistoryParams.toString()}`,
    {}
  )

  const normalizedRuns = loaded ? normalizeRunResponse(response) : []
  const runSource = normalizedRuns
  const profileOptions: { id: string; label: string }[] = response?.profile_options?.length
    ? response.profile_options
    : Array.from(
        new Map<string, { id: string; label: string }>(
          runSource.map((run) => [run.profile_id, { id: run.profile_id, label: run.profile }])
        ).values()
      )
  const runs = runSource

  const filterCount = [profileId !== 'all', status !== 'all', dateInputValue !== 'Today'].filter(Boolean).length
  const runSummary = response?.summary || {}
  const totalRuns = response?.count ?? runs.length
  const totalPages = response?.total_pages || 1
  const batchCount = runSummary.batches_created ?? runs.reduce((total, run) => total + run.batches_created, 0)
  const runningCount = runSummary.running ?? runs.filter((run) => run.statusFilter === 'running').length
  const failedCount = runSummary.failed ?? runs.filter((run) => run.statusFilter === 'failed').length
  const completedCount = runSummary.completed ?? runs.filter((run) => run.statusFilter === 'completed').length

  useEffect(() => {
    if (!runningCount) return
    const timer = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [runningCount])

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams)
    if (profileId === 'all') {
      nextParams.delete('profile')
    } else {
      nextParams.set('profile', profileId)
    }
    if (status === 'all') {
      nextParams.delete('status')
    } else {
      nextParams.set('status', status)
    }
    if (dateInputValue === 'Today') {
      nextParams.delete('start')
      nextParams.delete('end')
    } else {
      nextParams.set('start', selectedStartDate)
      nextParams.set('end', selectedEndDate)
    }
    if (page === 1) {
      nextParams.delete('page')
    } else {
      nextParams.set('page', String(page))
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true })
    }
  }, [dateInputValue, page, profileId, searchParams, selectedEndDate, selectedStartDate, setSearchParams, status])

  const detailResponseMatches = selectedRun?.api_id && String(detailResponse?.id) === selectedRun.api_id
  const detailRun = selectedRun && detailLoaded && detailResponseMatches ? normalizeRun(detailResponse) : selectedRun
  const detailOrders =
    detailResponseMatches && Array.isArray(detailResponse?.order_results) ? detailResponse.order_results : []
  const detailBatches: RunHistoryBatch[] =
    detailResponseMatches && Array.isArray(detailResponse?.batches) ? detailResponse.batches : []
  const detailOrderOutcomes = detailOrders.length ? summarizeOrderOutcomes(detailOrders) : []
  const detailBatchSummary = detailBatches.length
    ? summarizeRunBatches(detailBatches)
    : detailRun?.batch_breakdown || []
  const detailBatchPickDistances = detailBatches.filter((batch) => batch.pick_distance_feet != null)
  const detailPickDistance = detailRun ? (detailRun.average_pick_distance_feet ?? detailRun.pick_distance_feet) : null
  const clearFilters = () => {
    setProfileId('all')
    setStatus('all')
    setPage(1)
    setDateRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      },
    ])
    setDateInputValue('Today')
    setShowDateRange(false)
  }

  const handleDateRangeChange = (item: any) => {
    const nextRange = item.selection
    setPage(1)
    setDateRange([nextRange])
    setDateInputValue(`${formatDateInputValue(nextRange.startDate)} - ${formatDateInputValue(nextRange.endDate)}`)
  }

  const handleProfileChange = (value: string) => {
    setPage(1)
    setProfileId(value)
  }

  const handleStatusChange = (value: string) => {
    setPage(1)
    setStatus(value)
  }

  return (
    <RunHistoryLayout>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>
            Run History<small>{totalRuns} Runs</small>
          </h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar />
      </GlobalStyles.CardHeader>
      <div className="history-helper">
        Run History tracks wave reservation jobs and batch creation status. Batch productivity belongs in Batch History.
      </div>
      <div className="filter-bar">
        <div className="filter-group" style={{ minWidth: 230 }}>
          <label>Requested Date Range</label>
          <InputGroup className="date-input-group">
            <Form.Control
              type="text"
              value={dateInputValue}
              onFocus={() => setShowDateRange(true)}
              placeholder="Choose A Date"
              readOnly
            />
            <InputGroup.Text>
              <FontAwesomeIcon icon={faCalendarWeek} />
            </InputGroup.Text>
          </InputGroup>
        </div>
        <div className="filter-group" style={{ minWidth: 230 }}>
          <label>Profile</label>
          <Form.Select value={profileId} onChange={(event) => handleProfileChange(event.target.value)}>
            <option value="all">All Profiles</option>
            {profileOptions.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="filter-group" style={{ minWidth: 200 }}>
          <label>Status</label>
          <Form.Select value={status} onChange={(event) => handleStatusChange(event.target.value)}>
            <option value="all">All Statuses</option>
            <option value="queued">Queued</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </Form.Select>
        </div>
        <GlobalStyles.Button className="secondary filter-clear-button" onClick={clearFilters}>
          Clear {filterCount > 0 && <Badge pill>{filterCount}</Badge>}
        </GlobalStyles.Button>
      </div>
      {showDateRange ? (
        <>
          <div className="date-range-backdrop" onClick={() => setShowDateRange(false)} />
          <div className="date-range-picker">
            <DateRangePicker
              onChange={handleDateRangeChange}
              moveRangeOnFirstSelection={false}
              months={1}
              minDate={new Date(2018, 1, 1)}
              maxDate={new Date()}
              editableDateInputs={false}
              ranges={dateRange}
              direction="horizontal"
              className="hide-in-percy"
            />
          </div>
        </>
      ) : null}
      <div className="summary-row">
        <StatusBoxStyled className="silver summary-card">
          <p>Runs</p>
          <h4>{totalRuns}</h4>
        </StatusBoxStyled>
        <StatusBoxStyled className={runningCount ? 'blue summary-card' : 'silver summary-card'}>
          <p>Running</p>
          <h4>{runningCount}</h4>
        </StatusBoxStyled>
        <StatusBoxStyled className={failedCount ? 'red summary-card' : 'silver summary-card'}>
          <p>Failed</p>
          <h4>{failedCount}</h4>
        </StatusBoxStyled>
        <StatusBoxStyled className="green summary-card">
          <p>Completed</p>
          <h4>{completedCount}</h4>
        </StatusBoxStyled>
        <StatusBoxStyled className={batchCount ? 'blue summary-card' : 'silver summary-card'}>
          <p>Batches Created</p>
          <h4>{batchCount}</h4>
        </StatusBoxStyled>
      </div>
      <GlobalStyles.DataTableContainer>
        <div className="history-table-wrap">
          <GlobalStyles.DataTable className="history-table">
            <thead>
              <tr>
                <th>
                  <span className="text center">Run</span>
                </th>
                <th>
                  <span className="text center">Directive</span>
                </th>
                <th>
                  <span className="text center">Ran By</span>
                </th>
                <th>
                  <span className="text center">Created / Started</span>
                </th>
                <th>
                  <span className="text center">Duration</span>
                </th>
                <th>
                  <span className="text center">Order Coverage</span>
                </th>
                <th>
                  <span className="text center">Inventory Reserved</span>
                </th>
                <th>
                  <span className="text center">Batch Output</span>
                </th>
                <th>
                  <span className="text center">Pick Path</span>
                </th>
                <th>
                  <span className="text center">Status</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const createdDate = formatRunDate(run.requested_at)
                const startedDate = formatRunDate(run.started_at)
                const durationDisplay =
                  run.statusFilter === 'running'
                    ? formatElapsedDuration(run.started_at || run.requested_at, now)
                    : run.duration || 'Queued'
                const hasAveragePickDistance = run.average_pick_distance_feet != null
                const rowPickDistance = run.average_pick_distance_feet ?? run.pick_distance_feet
                return (
                  <React.Fragment key={run.id}>
                    <GlobalStyles.TableRow>
                      <TableCell center>
                        <button
                          className="run-link-button"
                          type="button"
                          onClick={() => setSelectedRun(selectedRun?.id === run.id ? null : run)}
                        >
                          {run.id}
                        </button>
                      </TableCell>
                      <TableCell center>
                        <div className="cell-primary strong">{run.directive}</div>
                        <div className="cell-secondary">{run.profile}</div>
                      </TableCell>
                      <TableCell center>{run.requested_by}</TableCell>
                      <TableCell center>
                        <div className="cell-secondary">
                          Created <strong>{createdDate.time}</strong> {createdDate.day}
                        </div>
                        <div className="cell-secondary">
                          {run.started_at ? (
                            <>
                              Started <strong>{startedDate.time}</strong> {startedDate.day}
                            </>
                          ) : (
                            'Not started'
                          )}
                        </div>
                      </TableCell>
                      <TableCell center>
                        <span className={run.statusFilter === 'running' ? 'duration-running' : ''}>
                          {durationDisplay}
                        </span>
                      </TableCell>
                      <TableCell center>
                        <div className="metric-list">
                          <div className="cell-primary strong">Selected {run.selected}</div>
                          <div className="detail-line">
                            Reserved {run.reserved}
                            {run.not_reserved ? ` · Short ${run.not_reserved}` : ''}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell center>
                        <div className="metric-list">
                          <div className="cell-primary strong">{run.units_reserved} units</div>
                          <div className="detail-line muted">{run.product_variants} variants</div>
                          {run.source_mix ? (
                            <div className="detail-group">
                              {run.source_mix.split(' / ').map((source) => (
                                <div key={source} className="detail-line muted">
                                  {source}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell center>
                        <div className="metric-list">
                          <div className="cell-primary strong">{run.batches_created} batches</div>
                          {run.batch_breakdown.length ? (
                            <div className="detail-group">
                              {run.batch_breakdown.map((batch) => (
                                <div key={batch.label} className="detail-line muted">
                                  {batch.label} {batch.value}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell center>
                        <div className="metric-list">
                          <div className="cell-primary strong">
                            {formatPickDistance(rowPickDistance)}
                            {hasAveragePickDistance ? ' avg' : rowPickDistance != null ? ' total' : ''}
                          </div>
                          <div className="detail-group">
                            {hasAveragePickDistance ? (
                              <div className="detail-line muted">
                                {formatPickDistance(run.pick_distance_feet)} total
                              </div>
                            ) : null}
                            <div className="detail-line muted">{run.locations_touched} locations</div>
                            <div className="detail-line muted">{run.zones_touched} zones</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell center>
                        <StatusBoxStyled
                          className={statusClassName(run.statusFilter)}
                          style={{ margin: '0 auto', width: 120 }}
                        >
                          {run.status}
                        </StatusBoxStyled>
                      </TableCell>
                    </GlobalStyles.TableRow>
                    {detailRun?.id === run.id ? (
                      <GlobalStyles.TableRow>
                        <TableCell colspan={10}>
                          <div className="run-detail-panel">
                            <div className="run-detail-header">
                              <div className="run-detail-title">
                                <h4>{detailRun.id} Run Detail</h4>
                                <p>
                                  {detailRun.directive} · {detailRun.profile}
                                </p>
                              </div>
                              <GlobalStyles.Button className="secondary" onClick={() => setSelectedRun(null)}>
                                Close
                              </GlobalStyles.Button>
                            </div>
                            {detailRun.statusFilter === 'failed' && detailRun.failure_reason ? (
                              <div className="run-detail-card">
                                <label>Failure Reason</label>
                                <div className="detail-line">{detailRun.failure_reason}</div>
                              </div>
                            ) : null}
                            {detailError ? <CardLoading error={detailError} /> : null}
                            <div className="run-detail-grid">
                              <div className="run-detail-card">
                                <label>Order Outcomes</label>
                                <div className="run-detail-list">
                                  {(detailOrderOutcomes.length
                                    ? detailOrderOutcomes
                                    : [
                                        { label: 'Reserved', value: detailRun.reserved },
                                        { label: 'Not Reserved', value: detailRun.not_reserved },
                                      ]
                                  ).map((item) => (
                                    <div key={item.label} className="detail-line">
                                      {item.label} {item.value}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="run-detail-card">
                                <label>Batch Output</label>
                                <div className="run-detail-list">
                                  {detailBatchSummary.length ? (
                                    detailBatchSummary.map((item) => (
                                      <div key={item.label} className="detail-line">
                                        {item.label} {item.value}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="detail-line muted">No batches created</div>
                                  )}
                                </div>
                              </div>
                              <div className="run-detail-card">
                                <label>Reservation Footprint</label>
                                <div className="run-detail-list">
                                  <div className="detail-line">{detailRun.units_reserved} units</div>
                                  <div className="detail-line muted">{detailRun.product_variants} variants</div>
                                  {detailRun.source_mix ? (
                                    <div className="detail-line muted">{detailRun.source_mix}</div>
                                  ) : null}
                                </div>
                              </div>
                              <div className="run-detail-card">
                                <label>Pick Path</label>
                                <div className="run-detail-list">
                                  <div className="detail-line">
                                    {formatPickDistance(detailPickDistance)}
                                    {detailRun.average_pick_distance_feet != null
                                      ? ' average'
                                      : detailPickDistance != null
                                        ? ' total'
                                        : ''}
                                  </div>
                                  {detailRun.average_pick_distance_feet != null ? (
                                    <div className="detail-line muted">
                                      {formatPickDistance(detailRun.pick_distance_feet)} total
                                    </div>
                                  ) : null}
                                  {detailRun.average_pick_distance_feet == null &&
                                  detailRun.pick_distance_feet != null ? (
                                    <div className="detail-line muted">Average unavailable for historical run</div>
                                  ) : null}
                                  <div className="detail-line muted">{detailRun.locations_touched} locations</div>
                                  <div className="detail-line muted">{detailRun.zones_touched} zones</div>
                                  {detailBatchPickDistances.map((batch, index) => (
                                    <div key={`${batch.batch || index}-pick-distance`} className="detail-line muted">
                                      {batchLabel(batch.batch_type || 'batch')} {index + 1}:{' '}
                                      {formatPickDistance(batch.pick_distance_feet)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </GlobalStyles.TableRow>
                    ) : null}
                  </React.Fragment>
                )
              })}
              {!runs.length ? (
                <GlobalStyles.TableRow>
                  <TableCell center colspan={10}>
                    No wave runs found.
                  </TableCell>
                </GlobalStyles.TableRow>
              ) : null}
            </tbody>
          </GlobalStyles.DataTable>
        </div>
        {loaded && totalPages > 1 ? (
          <div className="pagination-row">
            <Pages page={page} setPage={setPage} total={totalPages} show={5} />
          </div>
        ) : null}
        {!loaded && !error ? <CardLoading /> : null}
      </GlobalStyles.DataTableContainer>
      {error ? <CardLoading error={error} /> : null}
    </RunHistoryLayout>
  )
}
